/**
 * Orchestration for the AI Assistant page.
 *
 * Owns the request lifecycle; the store owns the data. Four behaviours in here
 * are the reason the page feels different from the old one:
 *
 * - **Streaming is throttled, not per-token.** Every chunk went straight to the
 *   store, one React commit per token, and a long answer janked the whole page
 *   while it arrived. Chunks now accumulate in a ref and flush on a ~60ms
 *   timer, so the text still appears live at a fraction of the render cost.
 *
 * - **Streaming degrades instead of failing.** A backend without
 *   `/chat/stream`, or a browser without a readable body, falls back to the
 *   plain `/chat` call once and remembers, rather than showing an error for
 *   something the user cannot act on.
 *
 * - **Stop keeps what arrived.** Abort is a deliberate act, so the partial
 *   answer stays on screen and is marked stopped — discarding it would throw
 *   away the part the user had already read.
 *
 * - **The in-flight request is aborted on unmount.** Navigating away mid-answer
 *   otherwise leaves a fetch writing into a store slice nobody is rendering.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useI18n } from '../../../i18n';
import { getLoggedInOwnerId } from '../../../utils/auth';
import {
  fetchAssistantStatus,
  fetchWorkspace,
  sendAssistantMessage,
  streamAssistantMessage,
} from '../api/assistantApi';
import useAssistantStore, { selectActiveThread } from '../store/useAssistantStore';
import { flushThreads } from '../utils/threadStorage';

/** How often streamed text is committed to the store. */
const FLUSH_INTERVAL_MS = 60;

const useAssistantHook = () => {
  const { t, lang } = useI18n();

  const threads = useAssistantStore((state) => state.threads);
  const activeThreadId = useAssistantStore((state) => state.activeThreadId);
  const activeThread = useAssistantStore(selectActiveThread);
  const streaming = useAssistantStore((state) => state.streaming);
  const error = useAssistantStore((state) => state.error);
  const includeContext = useAssistantStore((state) => state.includeContext);
  const workspace = useAssistantStore((state) => state.workspace);
  const workspaceLoading = useAssistantStore((state) => state.workspaceLoading);
  const status = useAssistantStore((state) => state.status);
  const drafts = useAssistantStore((state) => state.drafts);

  const {
    appendMessage,
    ensureThread,
    newThread,
    setError,
    setStatus,
    setStreaming,
    setWorkspace,
    setWorkspaceLoading,
    truncateFrom,
    updateMessage,
    setDraft,
    setEditingMessageId,
  } = useAssistantStore.getState();

  const ownerId = getLoggedInOwnerId();

  // Mutable request state. Refs, not store state: none of it should re-render.
  const abortRef = useRef(null);
  const bufferRef = useRef('');
  const flushTimerRef = useRef(null);
  const targetRef = useRef(null); // { threadId, messageId }
  const streamUnsupportedRef = useRef(false);
  const mountedRef = useRef(true);

  // ---- bootstrap -----------------------------------------------------------

  const loadWorkspace = useCallback(async () => {
    if (!ownerId) return;
    setWorkspaceLoading(true);
    try {
      const data = await fetchWorkspace(ownerId);
      if (mountedRef.current) setWorkspace(data);
    } catch (loadError) {
      // The panel and the contextual prompts degrade to the generic set; the
      // chat itself is unaffected, so this is not worth a visible error.
      if (mountedRef.current) setWorkspace(null);
    } finally {
      if (mountedRef.current) setWorkspaceLoading(false);
    }
  }, [ownerId, setWorkspace, setWorkspaceLoading]);

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    (async () => {
      const report = await fetchAssistantStatus(ownerId);
      if (!cancelled && mountedRef.current && report) setStatus(report);
    })();
    loadWorkspace();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (flushTimerRef.current) window.clearTimeout(flushTimerRef.current);
      // Abort any answer still arriving — see the note at the top of the file.
      abortRef.current?.abort();
      // A route change inside the SPA fires no `pagehide`, so the debounced
      // write has to be forced out here or the last ~400ms of the conversation
      // is lost by navigating to another screen.
      flushThreads();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId]);

  // ---- streaming plumbing --------------------------------------------------

  /** Commit buffered chunks to the store. */
  const flush = useCallback(() => {
    flushTimerRef.current = null;
    const target = targetRef.current;
    if (!target || !bufferRef.current) return;
    const text = bufferRef.current;
    updateMessage(target.threadId, target.messageId, { content: text, pending: true });
  }, [updateMessage]);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = window.setTimeout(flush, FLUSH_INTERVAL_MS);
  }, [flush]);

  /** Cancel a pending flush and write the final text synchronously. */
  const finalise = useCallback(
    (patch) => {
      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      const target = targetRef.current;
      if (!target) return;
      updateMessage(target.threadId, target.messageId, { pending: false, ...patch });
    },
    [updateMessage],
  );

  /**
   * Run one turn: stream a reply into an already-created placeholder message.
   *
   * `history` is the transcript BEFORE this question — the backend appends
   * `message` itself, so including it here would duplicate the last turn.
   */
  const runTurn = useCallback(
    async ({ threadId, messageId, message, history }) => {
      const controller = new AbortController();
      abortRef.current = controller;
      bufferRef.current = '';
      targetRef.current = { threadId, messageId };
      setStreaming({ threadId, messageId });
      setError(null);

      const options = { message, history, ownerId, includeContext, lang };

      try {
        if (!streamUnsupportedRef.current) {
          const result = await streamAssistantMessage(options, {
            signal: controller.signal,
            onMeta: ({ engine, contextUsed }) =>
              updateMessage(threadId, messageId, { engine, contextUsed }),
            onDelta: (_chunk, full) => {
              bufferRef.current = full;
              scheduleFlush();
            },
          });

          const text = (result.text || '').trim();
          if (result.aborted) {
            finalise({ content: text || t('assistant.message.stopped'), stopped: true });
            return;
          }
          if (!text && result.error) {
            finalise({ content: result.error, error: true });
            setError(result.error);
            return;
          }
          finalise({
            content: text || t('assistant.message.empty'),
            engine: result.engine,
            contextUsed: result.contextUsed,
            // A stream that died partway keeps its text and says why.
            truncatedReason: result.error || null,
          });
          return;
        }

        // Non-streaming path.
        const result = await sendAssistantMessage(options);
        finalise({
          content: result.response,
          engine: result.engine,
          contextUsed: result.contextUsed,
        });
      } catch (turnError) {
        if (turnError?.code === 'NO_STREAM' && !streamUnsupportedRef.current) {
          // Remember, then retry this same turn unstreamed. Only ever once,
          // because the flag is set before the retry.
          streamUnsupportedRef.current = true;
          try {
            const result = await sendAssistantMessage(options);
            finalise({
              content: result.response,
              engine: result.engine,
              contextUsed: result.contextUsed,
            });
            return;
          } catch (fallbackError) {
            const text = fallbackError?.message || t('assistant.error.generic');
            finalise({ content: text, error: true });
            setError(text);
            return;
          }
        }
        const text = turnError?.message || t('assistant.error.generic');
        finalise({ content: text, error: true });
        setError(text);
      } finally {
        abortRef.current = null;
        targetRef.current = null;
        bufferRef.current = '';
        setStreaming(null);
      }
    },
    [
      finalise,
      includeContext,
      lang,
      ownerId,
      scheduleFlush,
      setError,
      setStreaming,
      t,
      updateMessage,
    ],
  );

  // ---- public actions ------------------------------------------------------

  /** Send a new question. */
  const send = useCallback(
    async (rawText) => {
      const text = (rawText || '').trim();
      if (!text || streaming) return;

      const threadId = ensureThread();
      // The transcript as it stands is the history for this turn.
      const history =
        useAssistantStore.getState().threads.find((thread) => thread.id === threadId)?.messages ||
        [];

      appendMessage(threadId, { role: 'user', content: text });
      const messageId = appendMessage(threadId, {
        role: 'assistant',
        content: '',
        pending: true,
      });
      setDraft(threadId, '');

      await runTurn({ threadId, messageId, message: text, history });
    },
    [appendMessage, ensureThread, runTurn, setDraft, streaming],
  );

  /** Stop the answer in flight, keeping whatever arrived. */
  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  /**
   * Re-answer the last question.
   *
   * Drops the existing answer and asks again with the same history, so the user
   * gets a genuinely fresh reply rather than a second one appended below.
   */
  const regenerate = useCallback(async () => {
    if (streaming) return;
    const thread = selectActiveThread(useAssistantStore.getState());
    if (!thread) return;

    const messages = thread.messages || [];
    const lastAssistantIndex = [...messages]
      .reverse()
      .findIndex((message) => message.role === 'assistant');
    if (lastAssistantIndex < 0) return;
    const index = messages.length - 1 - lastAssistantIndex;

    // The question that produced it.
    const question = [...messages.slice(0, index)].reverse().find((m) => m.role === 'user');
    if (!question) return;
    const questionIndex = messages.indexOf(question);

    truncateFrom(thread.id, messages[index].id);
    const history = messages.slice(0, questionIndex);
    const messageId = appendMessage(thread.id, {
      role: 'assistant',
      content: '',
      pending: true,
    });
    await runTurn({
      threadId: thread.id,
      messageId,
      message: question.content,
      history,
    });
  }, [appendMessage, runTurn, streaming, truncateFrom]);

  /**
   * Replace a question and re-answer from that point.
   *
   * Everything after the edited message goes, because an answer to the old
   * wording is no longer part of a coherent transcript.
   */
  const editAndResend = useCallback(
    async (messageId, newText) => {
      const text = (newText || '').trim();
      if (!text || streaming) return;
      const thread = selectActiveThread(useAssistantStore.getState());
      if (!thread) return;

      const messages = thread.messages || [];
      const index = messages.findIndex((message) => message.id === messageId);
      if (index < 0) return;

      const history = messages.slice(0, index);
      truncateFrom(thread.id, messageId);
      setEditingMessageId(null);

      appendMessage(thread.id, { role: 'user', content: text });
      const placeholderId = appendMessage(thread.id, {
        role: 'assistant',
        content: '',
        pending: true,
      });
      await runTurn({ threadId: thread.id, messageId: placeholderId, message: text, history });
    },
    [appendMessage, runTurn, setEditingMessageId, streaming, truncateFrom],
  );

  /** Retry after a failed turn — same as regenerate, but clears the banner. */
  const retry = useCallback(async () => {
    setError(null);
    await regenerate();
  }, [regenerate, setError]);

  const draft = activeThreadId ? drafts[activeThreadId] || '' : '';
  const setActiveDraft = useCallback(
    (text) => {
      const threadId = activeThreadId || ensureThread();
      setDraft(threadId, text);
    },
    [activeThreadId, ensureThread, setDraft],
  );

  return {
    // data
    threads,
    activeThread,
    activeThreadId,
    messages: activeThread?.messages || [],
    draft,
    error,
    streaming,
    isStreaming: !!streaming,
    workspace,
    workspaceLoading,
    status,
    includeContext,
    ownerId,
    // actions
    send,
    stop,
    regenerate,
    editAndResend,
    retry,
    newThread,
    setActiveDraft,
    loadWorkspace,
  };
};

export default useAssistantHook;
